import { useEffect, useState } from "react";
import { ChevronLeft, Bell } from "lucide-react";

/* Universal back-action header for the Galaxy S20 chassis.
   Performs a SMART one-level-up that mirrors the app's existing URL-param
   navigation (view -> studio -> hub -> home tab) and dispatches `popstate` so
   MyDashboard / MistSuite / useStudioParam re-hydrate cleanly. It never fights
   the per-screen back buttons because it drives the exact same transitions.

   Visibility: hidden on the base landing tabs (Command Centre + Feed) when at
   root depth, on the immersive Reels player, and whenever the bottom nav is
   hidden (immersive scroll states). */

const HOME_VIEWS = ["command", "feed"];
const TAB_LABEL = { me: "Profile", chat: "Chat", alix: "Alix", feed: "Feed", command: "Command" };

function readDepth() {
  const p = new URLSearchParams(window.location.search);
  return { view: p.get("view"), studio: p.get("studio"), hub: p.get("hub"), zone: p.get("zone") };
}

export default function AppHeader({ view, onHome, unread = 0, onBell }) {
  const [depth, setDepth] = useState(readDepth);

  useEffect(() => {
    const onPop = () => setDepth(readDepth());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // Re-sync depth whenever the top-level tab changes.
  useEffect(() => { setDepth(readDepth()); }, [view]);

  // Full-screen overlays that render their own header/back own the chrome.
  // Every hub view (CareerDesk / BlueprintStudio / BusinessDesk / HubDetail and
  // its sub-desks) ships its own back button, so the universal header must step
  // aside for ALL hubs — otherwise a duplicate back bar stacks on top and pushes
  // the page's real back button down the screen.
  const chromeOwned = !!depth.studio || !!depth.hub || !!depth.zone;
  const isHome = HOME_VIEWS.includes(view) && !depth.hub && !depth.studio && !depth.view && !depth.zone;
  // Hidden on the base landing (Command/Feed), inside chrome-owning overlays, and
  // on the immersive Reels player. Shown on secondary tabs (Me/Chat/Alix) + hubs.
  if (view === "reels" || view === "doc-reader" || chromeOwned || isHome) return null;

  const label = String(depth.hub || TAB_LABEL[view] || "System").replace(/_/g, " ");

  const goBack = () => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("hub")) {
      p.delete("hub"); p.delete("view"); // hub -> command dashboard grid
      const qs = p.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    onHome?.("command"); // secondary tab -> home
  };

  return (
    <div
      data-testid="app-back-header"
      className="shrink-0 w-full bg-white border-b-4 border-black flex items-center justify-between px-3 py-2 relative z-40"
    >
      <button
        onClick={goBack}
        data-testid="app-back-btn"
        aria-label="Go back"
        className="flex items-center gap-1 bg-[#F2C94C] border-2 border-black px-3 py-1.5 font-mono text-[10px] font-black uppercase tracking-widest text-black shadow-[2px_2px_0px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
      >
        <ChevronLeft size={13} strokeWidth={3} /> Back
      </button>
      <span
        data-testid="app-back-context"
        className="font-mono text-[10px] font-black uppercase tracking-widest bg-black text-white px-2 py-1 truncate max-w-[45%]"
      >
        {label}
      </span>
      {onBell && view === "me" && (
        <button
          onClick={onBell}
          data-testid="header-notif-bell"
          aria-label="Notifications"
          className="relative ml-2 w-8 h-8 rounded-full bg-white border-2 border-black grid place-items-center shadow-[2px_2px_0px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
        >
          <Bell size={15} strokeWidth={2.4} className="text-black" />
          {unread > 0 && (
            <span data-testid="header-notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white border border-black grid place-items-center font-mono text-[8px] font-black">{unread}</span>
          )}
        </button>
      )}
    </div>
  );
}
